import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { useAuthStore } from "@/stores/authStore";
import { authService } from "../services";
import { getPrimaryRole, normalizeRoles } from "../utils";

interface AuthProviderProps {
  children: React.ReactNode;
}

/**
 * AuthProvider validates the session on app load.
 *
 * If there's an access token in localStorage:
 * 1. Validates it by calling /auth/session
 * 2. If valid, updates the auth store with fresh user data
 * 3. If invalid, clears tokens and auth store
 *
 * This ensures the user state is always in sync with the server.
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const [isValidating, setIsValidating] = useState(true);
  const login = useAuthStore((s) => s.login);
  const logout = useAuthStore((s) => s.logout);
  const setHasHydrated = useAuthStore((s) => s.setHasHydrated);

  useEffect(() => {
    async function validateSession() {
      const accessToken = localStorage.getItem("tr_access_token");

      // No token - user is not logged in
      if (!accessToken) {
        setHasHydrated(true);
        setIsValidating(false);
        return;
      }

      try {
        // Validate session with the server
        const session = await authService.getSession();

        if (session.valid) {
          // Session is valid - update auth store with fresh data (prioritize highest role).
          // /auth/session only carries roles — /auth/me supplies the REAL
          // permission strings that useHasPermission gates UI on
          // (e.g. CHAT_MODERATE, CALENDAR_CREATE_EVENT).
          const userInfo = await authService.getCurrentUser();
          const role = getPrimaryRole(session.roles);
          const roles = normalizeRoles(session.roles);

          const user = {
            id: session.userId,
            email: session.email,
            name: session.email.split("@")[0] || "User",
            role,
            roles,
            authType: userInfo.authType,
            permissions: userInfo.permissions,
            emailVerified: userInfo.emailVerified,
            createdAt: userInfo.createdAt,
          };

          login(user);
        } else {
          // Session invalid - clear everything
          clearAuthState();
        }
      } catch {
        // Session validation failed (401 or network error) - clear auth state
        clearAuthState();
      } finally {
        setHasHydrated(true);
        setIsValidating(false);
      }
    }

    function clearAuthState() {
      localStorage.removeItem("tr_access_token");
      localStorage.removeItem("tr_refresh_token");
      logout();
    }

    void validateSession();
  }, [login, logout, setHasHydrated]);

  // Show loading while validating session
  if (isValidating) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Loader2 className="text-primary h-8 w-8 animate-spin" />
      </div>
    );
  }

  return <>{children}</>;
}
